import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Loader2, Save, History } from "lucide-react";
import { SopMarkdown } from "@/components/manual/SopMarkdown";
import { SourceRefsFooter } from "@/components/manual/SourceRefsFooter";
import { LinkAliasesEditor } from "@/components/admin/LinkAliasesEditor";
import { RelatedActionsEditor } from "@/components/admin/RelatedActionsEditor";
import { DomainSelect } from "@/components/admin/DomainSelect";



export default function ManualAdminEdit() {
  const { docId } = useParams<{ docId: string }>();
  const nav = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [doc, setDoc] = useState<any>(null);
  const [origStatus, setOrigStatus] = useState<string>("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [revisions, setRevisions] = useState<any[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (!docId) return;
    (async () => {
      const { data } = await supabase.from("sop_documents").select("*").eq("doc_id", docId).maybeSingle();
      setDoc(data);
      setOrigStatus(data?.status || "");
      const { data: revs } = await supabase
        .from("sop_revisions").select("*").eq("doc_id", docId).order("archived_at", { ascending: false });
      setRevisions(revs || []);
      setLoading(false);
    })();
  }, [docId]);

  async function doSave() {
    if (!doc) return;
    if (!doc.change_note || !doc.change_note.trim()) {
      toast({ title: "Change note required", variant: "destructive" });
      return;
    }
    const slugRaw = (doc.slug || "").trim().toLowerCase();
    if (slugRaw && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slugRaw)) {
      toast({ title: "Invalid slug", description: "Use lowercase letters, numbers, and hyphens only.", variant: "destructive" });
      return;
    }
    if (slugRaw) {
      const { data: clash } = await supabase
        .from("sop_documents")
        .select("id")
        .eq("collection", doc.collection)
        .eq("slug", slugRaw)
        .neq("id", doc.id)
        .maybeSingle();
      if (clash) {
        toast({ title: "Slug already in use", description: "Choose a different slug within this collection.", variant: "destructive" });
        return;
      }
    }
    setSaving(true);
    const { error } = await supabase
      .from("sop_documents")
      .update({
        title: doc.title,
        body_markdown: doc.body_markdown,
        domain: doc.domain,

        trigger_tags: doc.trigger_tags,
        link_aliases: Array.isArray(doc.link_aliases) ? doc.link_aliases : [],
        related_actions: Array.isArray(doc.related_actions) ? doc.related_actions : [],
        version: doc.version,

        status: doc.status,
        change_note: doc.change_note,
        slug: slugRaw || null,
        updated_by: user?.email || null,
      })
      .eq("id", doc.id);
    setSaving(false);
    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Saved" });
    supabase.functions.invoke("link-audit").catch(() => {});
    nav("/admin/library");
  }

  function handleSaveClick() {
    if (doc?.requires_founder_review && doc.status === "published" && origStatus !== "published") {
      setConfirmOpen(true);
    } else {
      doSave();
    }
  }

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!doc) return <div className="p-8 text-center text-muted-foreground">Not found.</div>;

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/admin/library"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Link>
      </Button>

      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="font-mono">{doc.doc_id}</Badge>
        <Badge variant="secondary">{doc.domain}</Badge>
        {doc.requires_founder_review && <Badge variant="outline">founder review</Badge>}
      </div>

      <Tabs defaultValue="edit">
        <TabsList>
          <TabsTrigger value="edit">Edit</TabsTrigger>
          <TabsTrigger value="history"><History className="h-3.5 w-3.5 mr-1" /> History ({revisions.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="edit" className="space-y-4">
          {doc.human_edited && (
            <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-xs">
              This document carries accepted reviewer edits{doc.last_human_edit_at ? ` (${new Date(doc.last_human_edit_at).toLocaleDateString()})` : ""}. Edit surgically; do not regenerate.
            </div>
          )}
          <Card className="p-4 md:p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Title</Label>
                <Input value={doc.title || ""} onChange={(e) => setDoc({ ...doc, title: e.target.value })} />
              </div>
              <div>
                <Label>Category (domain)</Label>
                <DomainSelect
                  value={doc.domain || null}
                  onChange={(v) => setDoc({ ...doc, domain: v })}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Members see this as the library section header. The set is enforced by the database.
                </p>
              </div>
              <div>
                <Label>Version</Label>
                <Input value={doc.version || ""} onChange={(e) => setDoc({ ...doc, version: e.target.value })} />
              </div>

              <div>
                <Label>Status</Label>
                <Select value={doc.status} onValueChange={(v) => setDoc({ ...doc, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="retired">Retired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Trigger tags (semicolon-separated)</Label>
                <Input value={doc.trigger_tags || ""} onChange={(e) => setDoc({ ...doc, trigger_tags: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>URL slug</Label>
                <Input
                  value={doc.slug || ""}
                  onChange={(e) => setDoc({ ...doc, slug: e.target.value })}
                  placeholder="lowercase-with-hyphens"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  The address for this document. Changing it breaks any shared links pointing at the old slug. Old ID-based links keep working through a permanent redirect.
                </p>
              </div>
              <div className="md:col-span-2">
                <Label>Link aliases</Label>
                <LinkAliasesEditor
                  value={(doc.link_aliases as string[] | null) || []}
                  onChange={(v) => setDoc({ ...doc, link_aliases: v })}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Phrases that resolve to this document, deterministically. Retitles automatically add the previous title here so existing references keep working.
                </p>
              </div>
              <div className="md:col-span-2">
                <Label>Next steps (related actions)</Label>
                <RelatedActionsEditor
                  value={Array.isArray(doc.related_actions) ? doc.related_actions : []}
                  onChange={(v) => setDoc({ ...doc, related_actions: v })}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Buttons shown under this document for members. Only add what the body explicitly names. Documents render by title, never by code.
                </p>
              </div>

            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <Label>Body (Markdown)</Label>
                <Textarea
                  value={doc.body_markdown || ""}
                  onChange={(e) => setDoc({ ...doc, body_markdown: e.target.value })}
                  className="min-h-[500px] font-mono text-sm"
                />
              </div>
              <div>
                <Label>Live preview</Label>
                <div className="border rounded-md p-4 min-h-[500px] max-h-[500px] overflow-auto bg-background">
                  {doc.title && (
                    <h1 className="grt-article-title font-serif">{doc.title}</h1>
                  )}
                  <SopMarkdown body={doc.body_markdown || ""} />
                </div>
              </div>
            </div>

            {Array.isArray(doc.source_refs) && doc.source_refs.length > 0 && (
              <SourceRefsFooter refs={doc.source_refs} title="Call references" />
            )}

            <div>
              <Label>Change note <span className="text-destructive">*</span></Label>
              <Textarea
                value={doc.change_note || ""}
                onChange={(e) => setDoc({ ...doc, change_note: e.target.value })}
                placeholder="What changed and why?"
                required
              />
            </div>

            <div className="flex justify-end">
              <Button onClick={handleSaveClick} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save
              </Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card className="p-4 divide-y">
            {revisions.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">No prior revisions.</div>}
            {revisions.map((r) => {
              const key = `${r.doc_id}-${r.version}-${r.archived_at}`;
              const open = expanded === key;
              return (
                <div key={key} className="py-3">
                  <button
                    className="w-full text-left flex items-center justify-between gap-2"
                    onClick={() => setExpanded(open ? null : key)}
                  >
                    <div>
                      <div className="text-sm font-medium">v{r.version} — {r.title}</div>
                      <div className="text-xs text-muted-foreground">
                        {r.archived_at ? new Date(r.archived_at).toLocaleString() : ""} {r.archived_by ? `· ${r.archived_by}` : ""}
                      </div>
                      {r.change_note && <div className="text-xs italic text-muted-foreground mt-1">{r.change_note}</div>}
                    </div>
                    <span className="text-xs text-muted-foreground">{open ? "Hide" : "View"}</span>
                  </button>
                  {open && (
                    <div className="mt-3 border-l-2 pl-4">
                      <SopMarkdown body={r.body_markdown || ""} />
                    </div>
                  )}
                </div>
              );
            })}
          </Card>
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Founder review required</AlertDialogTitle>
            <AlertDialogDescription>
              This document requires founder review before publish. Confirm founder approval has been given.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirmOpen(false); doSave(); }}>Confirm & Publish</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
